import _ from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { xml2js } from "../json/xml2js";
import { paginate } from "../scripts/helperScripts";
import { getFieldOptions, getFieldValue } from "./../scripts/formScriptsv2";
import ExpandDiv from "./common/ExpandDiv";
import Input from "./common/Input";
import Pagination from "./common/Pagination";
import Table from "./common/Table";
import { lookupHighestVersion } from "./forms/forms";
import { getFormsySubmissionsByFormId } from "./services/soar";

function DeveloperChangeRequests() {
  const [requests, setRequests] = useState([]);
  const [loadStatus, setLoadStatus] = useState({
    loading: false,
    loaded: false,
  });

  const [beginDate, setBeginDate] = useState(
    moment().subtract(1, "months").format(moment.HTML5_FMT.DATE)
  );
  const [endDate, setEndDate] = useState(
    moment().format(moment.HTML5_FMT.DATE)
  );

  const [totalCount, setTotalCount] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState({
    path: "CreatedOn",
    order: "desc",
  });
  const [statusFilter, setStatusFilter] = useState("All");
  const [formInfo, setFormInfo] = useState({});

  const isMountedRef = useRef(true);
  const pageSize = 10;

  const loadRequests = async () => {
    setLoadStatus({ loading: true, loaded: false });
    const toastId = toast.loading("Loading Requests...", {
      type: toast.TYPE.INFO,
    });
    const response = await getFormsySubmissionsByFormId(
      "835B212A-49EC-EE11-8076-DC98408D5418"
    );

    if (!isMountedRef.current) {
      return;
    }

    if (!response) {
      toast.update(toastId, {
        render: "An error occurred while loading the Requests.",
        isLoading: false,
        type: toast.TYPE.ERROR,
        autoClose: 5000,
        closeOnClick: true,
      });
      setRequests([]);
      return;
    }

    setRequests(response.filter((request) => request.Active === "1"));
    setLoadStatus({ loaded: true, loading: false });

    if (response.length > 0) {
      toast.update(toastId, {
        render: "Requests loaded.",
        isLoading: false,
        type: toast.TYPE.SUCCESS,
        autoClose: 5000,
        closeOnClick: true,
      });
    } else {
      toast.update(toastId, {
        render: "No Requests available for selected date range.",
        isLoading: false,
        type: toast.TYPE.WARNING,
        autoClose: 5000,
        closeOnClick: true,
      });
    }
  };

  useEffect(() => {
    loadRequests();

    const getFormInfo = async () => {
      const response = await lookupHighestVersion("DeveloperChangeRequest");
      setFormInfo(response);
    };

    getFormInfo();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSort = (sortColumn) => {
    setSortColumn(sortColumn);
  };

  const getPagedData = useCallback(() => {
    if (!loadStatus.loaded || !requests?.filter) {
      return [];
    }

    const filtered = requests.filter((request) => {
      if (
        statusFilter !== "All" &&
        getFieldValue(request.FormResults.results.Status) !== statusFilter
      ) {
        return false;
      }

      if (statusFilter === "All" || statusFilter === "Live") {
        const createdOn = moment(request.CreatedOn).startOf("d");
        const begin = beginDate ? moment(beginDate) : undefined;
        const end = endDate ? moment(endDate) : undefined;

        if (begin && !begin.isSameOrBefore(createdOn)) {
          return false;
        }

        if (end && !end.isSameOrAfter(createdOn)) {
          return false;
        }
      }

      return true;
    });

    const tableData = filtered.map((request) => {
      const results = request.FormResults.results;
      return {
        SubmissionId: request.SubmissionId,
        FormVersionId: request.FormVersionId,
        VersionIntId: request.VersionIntId,
        Type: getFieldValue(results.Type),
        Title: getFieldValue(results.Title) || getFieldValue(results.Source),
        Source: getFieldValue(results.Source),
        Status: getFieldValue(results.Status),
        Task: getFieldValue(results.Task),
        CreatedBy: request.CreatedByName,
        CreatedOn: moment(request.CreatedOn).format("L"),
      };
    });

    const sorted = _.orderBy(tableData, [sortColumn.path], [sortColumn.order]);

    if (totalCount !== sorted.length) setTotalCount(sorted.length);

    return paginate(sorted, currentPage, pageSize);
  }, [
    requests,
    sortColumn,
    statusFilter,
    currentPage,
    totalCount,
    beginDate,
    endDate,
    loadStatus,
  ]);

  const columns = [
    {
      key: "Title",
      label: "Title",
      path: "Title",
      content: (request) => request.Title || request.Source,
    },
    {
      key: "Type",
      path: "Type",
      label: "Type",
    },
    {
      key: "Status",
      path: "Status",
      label: "Status",
    },
    {
      key: "Task",
      path: "Task",
      label: "Task #",
    },
    {
      key: "CreatedBy",
      path: "CreatedBy",
      label: "Created By",
    },
    {
      key: "CreatedOn",
      path: "CreatedOn",
      label: "Created On",
    },
    {
      key: "Open",
      content: (request) => {
        return (
          <Link
            title="Open Developer Change Request"
            to={`/${
              request.VersionIntId > 1
                ? `DeveloperChangeRequestv${request.VersionIntId}`
                : "DeveloperChangeRequest"
            }/${request.FormVersionId}/${request.SubmissionId}`}
          >
            <i className="fas fa-pen" />
          </Link>
        );
      },
    },
  ];

  const formConfig = useMemo(() => {
    if (!formInfo?.formData?.Versions?.[0]) return;

    return xml2js(formInfo.formData.Versions[0].FormConfiguration, {
      compact: true,
      spaces: 4,
    }).config;
  }, [formInfo]);

  const statusFilterOptions = useMemo(() => {
    return getFieldOptions(formConfig?.Status._attributes);
  }, [formConfig]);

  return (
    <ExpandDiv
      className="border p-2 mb-4 card-safy mt-2"
      title="Developer Requests"
      expandHandler={(expanded) => {
        if (!expanded || loadStatus.loaded || loadStatus.loading) {
          return;
        }

        loadRequests();
      }}
      preserveChildren={true}
      expandStatus={true}
    >
      <div className="container">
        <div className="row">
          <div className="col-6">
            <div className="mt-2">Filter by Status</div>
            {statusFilterOptions.map((option) => (
              <button
                key={option._id}
                className={`btn m-1 ${
                  option._id === statusFilter ? "btn-success" : "btn-safy-info"
                }`}
                onClick={() => {
                  setStatusFilter((sf) =>
                    sf === option._id ? "All" : option._id
                  );
                }}
              >
                {option.name}
              </button>
            ))}
          </div>
          {statusFilter === "All" || statusFilter === "Live" ? (
            <>
              <div className="col">
                <Input
                  name="BeginDate"
                  label="Begin Date"
                  type="date"
                  value={beginDate}
                  onChange={({ currentTarget: input }) => {
                    setBeginDate(input.value);
                  }}
                />
              </div>
              <div className="col">
                <Input
                  name="EndDate"
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={({ currentTarget: input }) => {
                    setEndDate(input.value);
                  }}
                />
              </div>
            </>
          ) : null}
          <div className="col pt-2">
            <Link
              title="Developer Change Request"
              to={`/DeveloperChangeRequest${
                formInfo.formVersionNumber > 1
                  ? `v${formInfo.formVersionNumber}`
                  : ""
              }/${formInfo.formVersionId}`}
              className="btn btn-primary mt-4 float-end"
            >
              Developer Change Request
            </Link>
          </div>
        </div>
      </div>
      <Table
        columns={columns}
        data={getPagedData()}
        sortColumn={sortColumn}
        onSort={handleSort}
        idColumn="SubmissionId"
      />
      {loadStatus.loaded ? (
        requests.length === 0 ? (
          "No requests for the given date range..."
        ) : (
          <div className="row justify-content-center">
            <Pagination
              itemsCount={totalCount}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        )
      ) : null}
    </ExpandDiv>
  );
}

export default DeveloperChangeRequests;
